import React from 'react';
import { Handle, Position } from 'reactflow';
import { themeStyles } from './ArtemisStyles';

// Simple code renderer without HTML injection
const CodeRenderer = ({ code }) => {
  return (
    <pre style={{ 
      margin: 0, 
      fontSize: '11px', 
      lineHeight: '1.4',
      color: '#E8E8E8',
      fontFamily: '"JetBrains Mono", "Consolas", monospace'
    }}>
      {code}
    </pre>
  );
};

// Custom node for showing code with syntax highlighting
export const CodeDisplayNode = ({ data }) => {
  return (
    <div style={{
      ...themeStyles.codeNode,
      overflow: 'hidden'
    }}>
      <div style={{ 
        fontSize: '11px', 
        marginBottom: '8px', 
        color: '#9CA3AF',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <span>{data.filename}</span>
        {data.score && (
          <span style={{
            background: data.score >= 4 ? '#28A745' : data.score >= 3 ? '#FD7E14' : '#DC3545',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            Score: {data.score}/5
          </span>
        )}
      </div>
      <CodeRenderer code={data.code} />
      {data.issues && (
        <div style={{
          marginTop: '8px',
          padding: '6px',
          background: 'rgba(220, 53, 69, 0.1)',
          borderRadius: '5px',
          fontSize: '10px',
          color: '#DC3545',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
        }}>
          ⚠️ {data.issues}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: '#6C757D' }} />
      <Handle type="target" position={Position.Left} style={{ background: '#6C757D' }} />
    </div>
  );
};

// Custom node for showing evolution process
export const EvolutionNode = ({ data }) => {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #FFD93D 0%, #FFB344 100%)',
      border: '2px solid #FFB344',
      borderRadius: '15px',
      padding: '20px',
      color: '#1A1A1A',
      width: '300px',
      textAlign: 'center',
      position: 'relative',
      boxShadow: '0 6px 20px rgba(255, 179, 68, 0.3)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '10px' }}>
        🧬 Generation {data.generation}
      </div>
      <div style={{ fontSize: '14px', marginBottom: '15px', fontWeight: '500' }}>
        {data.description}
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {data.scores && Object.entries(data.scores).map(([key, value]) => (
          <div key={key} style={{
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '20px',
            padding: '5px 10px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {key}: {value}
          </div>
        ))}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: '#FFB344' }} />
      <Handle type="target" position={Position.Left} style={{ background: '#FFB344' }} />
    </div>
  );
};

// Custom node for repository-wide optimization
export const RepoFileNode = ({ data }) => {
  return (
    <div style={{
      background: data.selected ? 
        'linear-gradient(135deg, #FFD93D 0%, #FFB344 100%)' : 
        'linear-gradient(135deg, #5E60CE 0%, #4458DC 100%)',
      border: `2px solid ${data.selected ? '#FFB344' : '#4458DC'}`,
      borderRadius: '10px',
      padding: '12px',
      color: data.selected ? '#1A1A1A' : 'white',
      fontSize: '13px',
      fontWeight: '600',
      width: '160px',
      textAlign: 'center',
      position: 'relative',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ fontWeight: '700', marginBottom: '5px' }}>
        {data.icon} {data.filename}
      </div>
      {data.optimization && (
        <div style={{ 
          fontSize: '11px', 
          background: 'rgba(255,255,255,0.2)', 
          borderRadius: '10px',
          padding: '3px 6px',
          marginTop: '5px',
          fontWeight: '500'
        }}>
          {data.optimization}
        </div>
      )}
      {data.impact && (
        <div style={{ 
          fontSize: '10px',
          marginTop: '5px',
          fontWeight: '700',
          color: data.selected ? 
            (data.impact === 'High' ? '#198754' : 
             data.impact === 'Medium' ? '#FD7E14' : '#FFC107') :
            (data.impact === 'High' ? '#6BCF7F' : 
             data.impact === 'Medium' ? '#FFB344' : '#FFE66D')
        }}>
          Impact: {data.impact}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: data.selected ? '#FFB344' : '#4458DC' }} />
      <Handle type="target" position={Position.Left} style={{ background: data.selected ? '#FFB344' : '#4458DC' }} />
    </div>
  );
};

// Custom node for file groups in multi-file evolution
export const FileGroupNode = ({ data }) => {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
      border: '2px solid #764BA2',
      borderRadius: '15px',
      padding: '15px',
      color: 'white',
      width: '200px',
      position: 'relative',
      boxShadow: '0 4px 15px rgba(118, 75, 162, 0.3)',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '10px' }}>
        {data.label}
      </div>
      {data.files && (
        <div style={{ fontSize: '12px' }}>
          {data.files.map((file, idx) => (
            <div key={idx} style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '8px',
              padding: '4px 8px',
              marginBottom: '4px',
              fontWeight: '500'
            }}>
              {file.icon} {file.name}
            </div>
          ))}
        </div>
      )}
      {data.combinedScore && (
        <div style={{
          marginTop: '10px',
          padding: '6px',
          background: 'rgba(107, 207, 127, 0.2)',
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: '700',
          textAlign: 'center'
        }}>
          Combined Score: {data.combinedScore}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: '#764BA2' }} />
      <Handle type="target" position={Position.Left} style={{ background: '#764BA2' }} />
    </div>
  );
};

// Collapsible code display node
export const CollapsibleCodeNode = ({ data }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // Show only first 3 lines when collapsed
  const getPreviewCode = (code) => {
    const lines = code.split('\n');
    if (lines.length <= 3) return code;
    return lines.slice(0, 3).join('\n') + '\n...';
  };

  return (
    <div style={{
      ...themeStyles.codeNode,
      overflow: 'hidden',
      minWidth: '300px',
      maxWidth: isExpanded ? '500px' : '300px',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ 
        fontSize: '11px', 
        marginBottom: '8px', 
        color: '#9CA3AF',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{data.filename}</span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'rgba(156, 163, 175, 0.2)',
              border: '1px solid rgba(156, 163, 175, 0.3)',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '10px',
              cursor: 'pointer',
              color: '#9CA3AF',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(156, 163, 175, 0.3)';
              e.target.style.borderColor = 'rgba(156, 163, 175, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(156, 163, 175, 0.2)';
              e.target.style.borderColor = 'rgba(156, 163, 175, 0.3)';
            }}
          >
            {isExpanded ? '▼ Collapse' : '▶ Expand'}
          </button>
        </span>
        {data.score && (
          <span style={{
            background: data.score >= 4 ? '#28A745' : data.score >= 3 ? '#FD7E14' : '#DC3545',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            Score: {data.score}/5
          </span>
        )}
      </div>
      <pre style={{ 
        margin: 0, 
        fontSize: '11px', 
        lineHeight: '1.4',
        maxHeight: isExpanded ? '400px' : '60px',
        overflow: isExpanded ? 'auto' : 'hidden',
        transition: 'max-height 0.3s ease',
        color: '#E8E8E8',
        fontFamily: '"JetBrains Mono", "Consolas", monospace'
      }}>
        {isExpanded ? data.code : getPreviewCode(data.code)}
      </pre>
      {!isExpanded && data.code.split('\n').length > 3 && (
        <div style={{
          textAlign: 'center',
          fontSize: '10px',
          color: '#6A7280',
          marginTop: '4px',
          fontStyle: 'italic'
        }}>
          {data.code.split('\n').length} lines total
        </div>
      )}
      {data.issues && (
        <div style={{
          marginTop: '8px',
          padding: '6px',
          background: 'rgba(220, 53, 69, 0.1)',
          borderRadius: '5px',
          fontSize: '10px',
          color: '#DC3545',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
        }}>
          ⚠️ {data.issues}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: '#6C757D' }} />
      <Handle type="target" position={Position.Left} style={{ background: '#6C757D' }} />
    </div>
  );
};

export const nodeTypes = {
  codeDisplay: CodeDisplayNode,
  collapsibleCode: CollapsibleCodeNode,
  evolution: EvolutionNode,
  repoFile: RepoFileNode,
  fileGroup: FileGroupNode,
}; 